/**
 * roundRobin(items): Return an array of rounds (weeks),
 * each round is an array of matchups: [{ home, away }, ...].
 *
 * If 'items.length' is odd, we add a "Bye" object.
 * This produces exactly (N - 1) rounds if items.length is even,
 * or N rounds if odd (because we added a "Bye" item).
 *
 * Example:
 *   const rounds = roundRobin(['A','B','C','D']);
 *   // rounds.length = 3 => each round has 2 matches
 *
 * Each round is formed by "fixing" the first item and rotating the rest.
 * Then we pair the "fixed" vs the front item, and pair up the rest inward.
 */
export function roundRobin(items) {
  // 1) If odd, push a "Bye" object
  const realItems = [...items];
  const hasBye = realItems.length % 2 !== 0;
  if (hasBye) {
    realItems.push({ id: null, name: "Bye" });
  }

  const n = realItems.length; // now guaranteed even
  const half = n / 2;

  // We'll build 'n - 1' rounds if even, or 'n - 1' if we added a bye
  const numRounds = n - 1;

  // 2) Keep the first item fixed, rotate the others
  const fixed = realItems[0];
  let others = realItems.slice(1);

  const rounds = [];

  // Generate each "round"
  for (let roundIndex = 0; roundIndex < numRounds; roundIndex += 1) {
    const matchups = [];

    // First matchup: fixed vs front of "others"
    matchups.push({ home: fixed, away: others[0] });

    // Then pair up the rest (inward from both ends)
    for (let i = 1; i < half; i += 1) {
      const home = others[i];
      const awayIndex = others.length - i;
      const away = others[awayIndex];
      matchups.push({ home, away });
    }

    rounds.push(matchups);

    // Rotate the array "others" by moving the last element to the front
    const lastElement = others[others.length - 1];
    others = [lastElement, ...others.slice(0, others.length - 1)];
  }

  return rounds;
}
